/**
 * Deep merge utility to replace lodash.merge
 * Recursively merges properties from source objects into target object
 * Later sources override earlier ones
 *
 * @example
 * const result = deepMerge(
 *   { a: { b: 1, c: 2 }, d: 3 },
 *   { a: { b: 10 }, e: 4 }
 * );
 * // result: { a: { b: 10, c: 2 }, d: 3, e: 4 }
 */
export function deepMerge<T extends Record<string, unknown>>(
  target: T,
  ...sources: (Record<string, unknown> | undefined)[]
): T {
  if (!target || typeof target !== 'object') {
    return target;
  }

  for (const source of sources) {
    if (!source || typeof source !== 'object') {
      continue;
    }

    for (const key in source) {
      if (!Object.prototype.hasOwnProperty.call(source, key)) {
        continue;
      }

      const sourceValue = source[key];
      const targetValue = (target as Record<string, unknown>)[key];

      // If both values are plain objects (not arrays or other types), merge recursively
      if (isPlainObject(targetValue) && isPlainObject(sourceValue)) {
        (target as Record<string, unknown>)[key] = deepMerge(
          targetValue as Record<string, unknown>,
          sourceValue as Record<string, unknown>
        );
      } else {
        // Otherwise, use the source value
        (target as Record<string, unknown>)[key] = sourceValue;
      }
    }
  }

  return target;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return (
    value !== null && typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date)
  );
}
